import React from 'react';
import styles from '../ProjectPhasesBar.module.scss';
import Tooltip from '../../../common/Tooltip.jsx';

export const getPeriodKey = (recurrenceType, date) => {
  const year = date.year();

  if (recurrenceType === 'weekly') {
    return `${year}_W${String(date.isoWeek()).padStart(2, "0")}`; // example: 2026_W01
  }
  if (recurrenceType === 'bi_weekly') {
    const week = Math.ceil(date.isoWeek() / 2);
    const firstWeekOfPair = (week - 1) * 2 + 1;
    return `${year}_W${String(firstWeekOfPair).padStart(2, "0")}_BI`; // example: 2026_W01_BI
  }
  if (recurrenceType === 'monthly') {
    return `${year}_M${String(date.month() + 1).padStart(2, "0")}`; // example: 2026_M01
  }
  if (recurrenceType === 'bi_monthly') {
    const month = date.month(); // 0-based
    const startMonth = month % 2 === 0 ? month : month - 1; // example: 2026_M01_BI
    return `${year}_M${String(startMonth + 1).padStart(2, '0')}_BI`;
  }
  if (recurrenceType === 'quarterly') {
    const quarter = Math.ceil((date.month() + 1) / 3); // example: 2026_Q1
    return `${year}_Q${quarter}`;
  }
  if (recurrenceType === 'every_2_years') {
    return `${year}_2Y` // example: 2026_2Y
  }
  return `${year}`; // example: 2026
};

export const getPeriodLabel = (recurrenceType, periodKey) => {
  let start;

  if (recurrenceType === 'weekly') {
    const [year, week] = periodKey.split("_W"); // example: 2026_W01
    start = dayjs().year(year).isoWeek(week).startOf('isoWeek');
    const weekOfYear = dayjs(start).isoWeek();
    return `${I18n.t('week_short')}${weekOfYear}, ${start.year()}`;
  }
  if (recurrenceType === 'bi_weekly') {
    const [year, weekPart] = periodKey.split("_W"); // example: 2026_W01_BI
    const week = weekPart.replace("_BI", "");
    start = dayjs().year(year).isoWeek(week).startOf('isoWeek');
    const weekOfYear = dayjs(start).isoWeek();
    return `${I18n.t('week_short')}${weekOfYear}, ${start.year()}`;
  }
  if (recurrenceType === 'monthly') {
    const [year, month] = periodKey.split("_M"); // example: 2026_M01
    start = dayjs(`${year}-${month}-01`);
    return `${new Date(start).toLocaleDateString(I18n.locale, {month: 'short'})}, ${start.year()}`;
  }
  if (recurrenceType === 'bi_monthly') {
    const [year, monthPart] = periodKey.split("_M"); // example: 2026_M01_BI
    const month = monthPart.replace("_BI", "");
    start = dayjs(`${year}-${month}-01`);
    return `${new Date(start).toLocaleDateString(I18n.locale, {month: 'short'})}, ${start.year()}`;
  }
  if (recurrenceType === 'quarterly') {
    const [year, quarter] = periodKey.split("_Q"); // example: 2026_Q1
    const startMonth = (Number(quarter) - 1) * 3;
    start = dayjs(`${year}-${startMonth + 1}-01`);
    const quarterOfYear = dayjs(start).quarter();
    return `${I18n.t('quarter_short')}${quarterOfYear}, ${start.year()}`;
  }
  if (recurrenceType === 'every_2_years') {
    const year = Number((periodKey || "").split("_")[0]); // example: 2026_2Y
    start = dayjs(`${year}-01-01`);
    return `${start.year()} – ${start.year() + 1}`;
  }
  return periodKey; // example: 2026
};

export const getNextDate = (recurrenceType, date) => {
  if (recurrenceType === 'weekly') return date.add(1, 'week');
  if (recurrenceType === 'bi_weekly') return date.add(2, 'week');
  if (recurrenceType === 'monthly') return date.add(1, 'month');
  if (recurrenceType === 'bi_monthly') return date.add(2, 'month');
  if (recurrenceType === 'quarterly') return date.add(3, 'month');
  if (recurrenceType === 'yearly') return date.add(1, 'year');
  if (recurrenceType === 'every_2_years') return date.add(2, 'year');
  return date;
};

function TimelineSteps({recurrenceType, startDate, endDate, timelineItems = [], setCollapsed, collapsed}) {
  if (!startDate || !endDate) {
    return (
      <div className={styles.Content}>
        <div className={styles.Status}>{I18n.t('missing_dates')}</div>
      </div>
    )
  }

  return (
    <div className={styles.timelineDots}>
      {timelineItems.map((item, index) =>
        item.type === 'ellipsis' ? (
          <span key={index} className={styles.ellipsis} onClick={() => setCollapsed(!collapsed)}>
            ...
          </span>
          ) :
          <Tooltip key={item.scheduled_period} title={getPeriodLabel(recurrenceType, item.scheduled_period)}>
            <div className={`${styles.dot} ${styles[item.status]}`}/>
          </Tooltip>
      )}

      {!collapsed &&
        <span className={`${styles.collapseLink} btn-link`} onClick={() => setCollapsed(true)}>
          {I18n.t('collapse')}
        </span>
      }
      <div className={styles.Content} style={{width: '100%', flex: 'auto'}}>
        <div className={styles.Status}>{I18n.t(recurrenceType)}</div>
      </div>
    </div>
  )
}

export default TimelineSteps;
